"use client";

import { useSyncExternalStore } from "react";
import type { TimelineEvent } from "@/app/climate/_components/ClimateTimeline";
import type { RescissionMotion } from "@/app/climate/_lib/data";

type Stop = {
  iso: string;
  dateLabel: string;
  caption: string;
  detail?: string;
  status:
    | "past-defeated"
    | "past-carried"
    | "past-unknown"
    | "today"
    | "upcoming";
  voteText?: string;
  motionRefs?: string[];
};

type Props = {
  /** The Sept 2025 (or later past) rescission-vote entry from timeline.json. */
  pastRescissionVote: TimelineEvent;
  /** All currently-public rescission motions (from rescission-motions.json). */
  motions: RescissionMotion[];
  /**
   * Initial date used for SSR + first paint. Sourced from an artefact's
   * `meta.generated_at` so server- and client-rendered HTML match on
   * hydration. After hydration, the strip swaps in the user's actual
   * current date — so even if a future May-5/26 vote happens and the
   * page hasn't been manually updated yet, the "today" stop and the
   * past/upcoming classifications still advance correctly for any
   * reader visiting after the date.
   */
  snapshotIso: string;
};

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateLabel(iso: string): string {
  // Accepts YYYY-MM-DD or YYYY-MM. Returns "Sep 16, 2025" or "May 2026".
  const parts = iso.split("-");
  const year = parts[0];
  const month = MONTHS[Number(parts[1]) - 1] ?? "";
  const day = parts[2];
  return day ? `${month} ${Number(day)}, ${year}` : `${month} ${year}`;
}

function isoDate(d: Date): string {
  // Local-timezone date so the "today" stop reads as the user's
  // calendar day rather than UTC's. Calgary is UTC-7/-6, so a UTC
  // implementation would show "tomorrow" for any reader visiting
  // after ~5pm local — the wrong answer for a "today" marker.
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Extract a vote tally like "4-10" from the past-vote summary prose. The
 * timeline.json summary text is the primary-source-verified record; we
 * read the tally back out of it rather than introducing a new structured
 * field. If the summary phrasing ever changes such that the regex misses,
 * the strip falls back to a label without the tally — never invents a
 * number.
 */
function extractTally(summary: string): string | undefined {
  const m = /\b(\d{1,2})-(\d{1,2})\b/.exec(summary);
  if (!m) return undefined;
  return `${m[1]}-${m[2]}`;
}

function StopMarker({ status }: { status: Stop["status"] }) {
  const base = {
    width: "1.6rem",
    height: "1.6rem",
    borderRadius: "999px",
    flex: "none",
  } as const;
  if (status === "past-defeated") {
    return (
      <span
        aria-hidden
        className="inline-flex items-center justify-center"
        style={{
          ...base,
          background: "color-mix(in oklab, var(--brick) 14%, transparent)",
          color: "var(--brick)",
          fontFamily: "var(--font-feature)",
          fontSize: "1.2rem",
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ×
      </span>
    );
  }
  if (status === "past-carried") {
    return (
      <span
        aria-hidden
        className="inline-flex items-center justify-center"
        style={{
          ...base,
          background: "color-mix(in oklab, var(--moss) 14%, transparent)",
          color: "var(--moss)",
          fontWeight: 700,
          fontSize: "0.9rem",
        }}
      >
        ✓
      </span>
    );
  }
  if (status === "past-unknown") {
    return (
      <span
        aria-hidden
        className="inline-flex items-center justify-center"
        style={{
          ...base,
          background: "var(--rule)",
          color: "var(--ink-3)",
          fontWeight: 600,
          fontSize: "1rem",
        }}
      >
        •
      </span>
    );
  }
  if (status === "today") {
    return (
      <span
        aria-hidden
        className="inline-flex items-center justify-center"
        style={{
          ...base,
          background: "var(--signal)",
          boxShadow: "0 0 0 4px color-mix(in oklab, var(--signal) 22%, transparent)",
        }}
      />
    );
  }
  // upcoming
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center"
      style={{
        ...base,
        background: "transparent",
        border: "2px solid var(--ink-3)",
      }}
    />
  );
}

function StopCard({ stop }: { stop: Stop }) {
  const tone =
    stop.status === "past-defeated"
      ? "var(--brick)"
      : stop.status === "past-carried"
        ? "var(--moss)"
        : stop.status === "today"
          ? "var(--signal)"
          : "var(--ink-3)";
  const statusText =
    stop.status === "past-defeated"
      ? `defeated ${stop.voteText ?? ""}`.trim()
      : stop.status === "past-carried"
        ? `carried ${stop.voteText ?? ""}`.trim()
        : stop.status === "past-unknown"
          ? "past · outcome pending"
          : stop.status === "today"
            ? "today"
            : "upcoming";
  return (
    <div className="flex flex-col gap-2 lg:flex-1 lg:min-w-0">
      <div className="flex items-center gap-3 lg:flex-col lg:items-start lg:gap-2">
        <StopMarker status={stop.status} />
        <div className="flex flex-col">
          <span className="cs-mono text-[11px] tracking-[0.16em] uppercase text-ink-3">
            {stop.dateLabel}
          </span>
          <span
            className="cs-mono text-[11px] tracking-[0.18em] uppercase font-semibold"
            style={{ color: tone }}
          >
            {statusText}
          </span>
        </div>
      </div>
      <div className="text-[15px] text-ink-1 leading-snug pl-[2.6rem] lg:pl-0">
        {stop.caption}
      </div>
      {stop.detail && (
        <div className="text-[13px] text-ink-3 leading-snug pl-[2.6rem] lg:pl-0">
          {stop.detail}
        </div>
      )}
      {stop.motionRefs && stop.motionRefs.length > 0 && (
        <div className="cs-mono text-[11px] tracking-[0.14em] uppercase text-ink-3 pl-[2.6rem] lg:pl-0">
          {stop.motionRefs.join(" · ")}
        </div>
      )}
    </div>
  );
}

export function VoteTimelineStrip({
  pastRescissionVote,
  motions,
  snapshotIso,
}: Props) {
  const tally = extractTally(pastRescissionVote.summary);

  // Server renders the snapshot date; client swaps in the user's actual
  // "today" once hydrated — so the "today" stop and past/upcoming
  // classifications stay correct even if a future vote happens and the
  // page hasn't been manually updated. useSyncExternalStore is the
  // canonical React 19 pattern for this two-phase SSR/CSR value: the
  // server snapshot keeps first paint identical to the SSR HTML (no
  // hydration mismatch), then the client snapshot takes over. The
  // subscribe noop is intentional — "today" doesn't change while the
  // tab is open in any way that this strip needs to react to.
  const initialIso = isoDate(new Date(snapshotIso));
  const todayIsoDate = useSyncExternalStore(
    () => () => {},
    () => isoDate(new Date()),
    () => initialIso,
  );

  // Group motions by EC date — both currently fall on the same day, but
  // the strip is built to handle multi-day futures cleanly.
  const ecDates = Array.from(
    new Set(motions.map((m) => m.executive_committee_date)),
  ).sort();
  const councilDates = Array.from(
    new Set(motions.map((m) => m.regular_council_date)),
  ).sort();

  const stops: Stop[] = [];

  // Past rescission vote
  stops.push({
    iso: pastRescissionVote.date,
    dateLabel: formatDateLabel(pastRescissionVote.date),
    caption: pastRescissionVote.label,
    detail: "EC2025-0859 · last rescission attempt",
    status: "past-defeated",
    voteText: tally,
  });

  // Today
  stops.push({
    iso: todayIsoDate,
    dateLabel: formatDateLabel(todayIsoDate),
    caption: "Today",
    status: "today",
  });

  // Upcoming Executive Committee dates. If the meeting date is already
  // behind the snapshot we mark it `past-unknown` (neutral grey marker)
  // rather than `past-defeated` — the strip cannot claim an outcome it
  // does not have. A subsequent edit that adds a primary-source-verified
  // outcome (e.g. a new timeline entry) is what would promote the marker
  // to defeated/carried.
  for (const ec of ecDates) {
    const ecMotions = motions.filter((m) => m.executive_committee_date === ec);
    const refs = ecMotions.map((m) => m.report_number);
    const status: Stop["status"] =
      ec < todayIsoDate ? "past-unknown" : "upcoming";
    stops.push({
      iso: ec,
      dateLabel: formatDateLabel(ec),
      caption: "Executive Committee",
      detail: `${ecMotions.length} rescission motion${ecMotions.length === 1 ? "" : "s"} on the agenda`,
      status,
      motionRefs: refs,
    });
  }

  // Regular Council dates (only if forwarded by EC). Same `past-unknown`
  // policy applies once the date is behind the snapshot without a verified
  // outcome.
  for (const c of councilDates) {
    const cMotions = motions.filter((m) => m.regular_council_date === c);
    const refs = cMotions.map((m) => m.report_number);
    const status: Stop["status"] =
      c < todayIsoDate ? "past-unknown" : "upcoming";
    stops.push({
      iso: c,
      dateLabel: formatDateLabel(c),
      caption: "Regular Council",
      detail: "if forwarded by Executive Committee",
      status,
      motionRefs: refs,
    });
  }

  // Sort by ISO date so the strip reads chronologically regardless of
  // which inputs supplied which stop.
  stops.sort((a, b) => (a.iso < b.iso ? -1 : a.iso > b.iso ? 1 : 0));

  return (
    <section className="not-prose mb-10">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div className="cs-mono text-ink-3 text-[11px] tracking-[0.18em] uppercase">
          the vote, in context
        </div>
        <div className="cs-mono text-ink-3 text-[11px] tracking-[0.16em] uppercase hidden sm:block">
          chronology
        </div>
      </div>
      <div
        className="rounded-2xl p-5 sm:p-6"
        style={{
          background: "var(--surface)",
          border: "1px solid var(--rule-2)",
        }}
      >
        <div className="flex flex-col lg:flex-row lg:items-start gap-6 lg:gap-4 relative">
          {stops.map((s, i) => (
            <div key={`${s.iso}-${i}`} className="contents">
              <StopCard stop={s} />
              {i < stops.length - 1 && (
                <div
                  aria-hidden
                  className="hidden lg:block self-start mt-[0.7rem]"
                  style={{
                    flex: "0 0 auto",
                    width: "2rem",
                    borderTop: "1px dashed var(--rule-2)",
                  }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
